import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { HTTPErrorResponse } from '@ts/ui-models';
import * as Sentry from '@sentry/react';
import { deepRedact, sanitizeAxiosError } from './sentrySanitization';

const App = require('@gral/app');

const getHeader = () => {
  const token = App.cxStore.getToken();

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const reportErrorToSentry = (error: AxiosError | any, extraInfoError: any) => {
  const newError = Error('Error Api Errors');

  Sentry.captureException(newError, {
    level: 'error',
    tags: {
      section: 'API Errors',
    },
    extra: {
      error: sanitizeAxiosError(error),
      user: deepRedact({
        id: App.cxStore.getUser()?.id,
        email: App.cxStore.getUser()?.email,
      }),
      extraInfoError: safeStringify(deepRedact(extraInfoError)),
    },
  });
};

export const checkErrorRequest = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  key: string | undefined,
  error: AxiosError | any,
  retryThunk: any,
  extraInfoError: any
) => {
  if (!error) return;

  console.log('error', error);
  const errorResponse = error?.response;
  const status = errorResponse?.status;

  const retry = async () => {
    await renewToken(dispatch);
    return dispatch(retryThunk);
  };

  const backendError: HTTPErrorResponse = errorResponse?.data;
  const isBackendObject = typeof backendError === 'object' && backendError !== null;

  switch (status) {
    case 400:
    case 404: {
      if (isBackendObject) {
        return controlBackendError(dispatch, retryThunk, backendError);
      }
      return;
    }
    case 401:
      if (isBackendObject) {
        const code = backendError.error?.code;

        switch (code) {
          case 'SESSION_EXPIRED': {
            return retry();
          }
          case 'INVALID_TOKEN':
          case 'MISSING_AUTHENTICATION': {
            await App.cxStore._errorRenewToken(); //Future in redux or new storage
            dispatch(globalActions.logout());
            dispatch(globalActions.notification('Your session has expired. Please log in again'));
            return;
          }
          default:
            break;
        }
      } else {
        return retry();
      }

      break;
    case 429:
      return;
    default: {
      //500
      if (!key && (error.error?.message || error._gral)) {
        dispatch(globalActions.notification(error.error?.message ?? error._gral));
        return;
      }

      if (key) return;

      if (error.code !== 'ERR_NETWORK') reportErrorToSentry(error, extraInfoError);
      return;
    }
  }

  return controlBackendError(dispatch, retryThunk, backendError);
  //throw new Error(error);
};

let limitRenewToken = 0;

export const renewToken = async dispatch => {
  try {
    const refreshToken = App.cxStore.getUser().refreshToken;

    if (refreshToken && limitRenewToken < 5) {
      const response = await axios.post(
        '/api/v2/refreshToken',
        { refreshToken: refreshToken },
        {
          method: 'post',
          headers: getHeader(),
        }
      );
      const data = response.data;
      await App.cxStore._refreshTokenSuccess({ data });
      limitRenewToken++;
    } else {
      limitRenewToken = 0;
      App.cxStore._errorRenewToken();
      dispatch?.(globalActions.logout());
      dispatch?.(globalActions.notification('Your session has expired. Please log in again'));
    }
  } catch (error) {
    App.cxStore._errorRenewToken();
    dispatch?.(globalActions.logout());
    dispatch?.(globalActions.notification('Your session has expired. Please log in again'));
    throw new Error();
  }
};

const controlBackendError = async (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  retryThunk,
  error?: HTTPErrorResponse
) => {
  console.log('controlBackendError', error);
  if (error?.error?.message) {
    dispatch(globalActions.notification(error?.error?.message));
    return;
  }
  /* return await renewToken(dispatch).then(async () => {
    return await dispatch(retryThunk);
  }); */
};

export const safeStringify = (obj, depth = 3) => {
  let cache = new Set();
  const stringifyFn = (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.has(value)) {
        // Circular reference found, discard key
        return '[Circular]';
      }
      cache.add(value);
    }
    return value;
  };

  const limitedStringify = (value, currentDepth) => {
    if (currentDepth === 0 || value === null || typeof value !== 'object') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(item => limitedStringify(item, currentDepth - 1));
    }

    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, limitedStringify(val, currentDepth - 1)]));
  };

  return JSON.stringify(limitedStringify(obj, depth), stringifyFn, 2);
};
